import React, { useState } from 'react';
import moment from 'moment';
import { StackShim as Stack } from '@fluentui/react-migration-v8-v9';
import { Select, Label, FluentProvider, webLightTheme, Switch } from '@fluentui/react-components';
import { DatePicker } from '@fluentui/react-datepicker-compat';
import IDropdownOption from '../../../utils/types/IDropdownOption';
import momentUtc from '../../../utils/DateFormatter';
import StatDateFormats from '../../../utils/types/DateFormats';

interface IProcessSearchFiscalYearPickerProps {
  label: string;
  onUpdateDate: (startDate: Date, endDate: Date, selectedDateRangeOption: string) => void;
  initialSelectedOption: string;
  initialStartDate?: Date;
  initialEndDate?: Date;
}
const ProcessSearchFiscalYearPicker = (props: IProcessSearchFiscalYearPickerProps): JSX.Element => {
  const { label, onUpdateDate, initialSelectedOption, initialStartDate, initialEndDate } = props;
  const labelSlug = label.toLowerCase().replace(/[^a-z0-9]+/g, '-');
  const [idSuffix] = useState(() => Math.random().toString(36).slice(2, 8));
  const baseId = `${labelSlug}-${idSuffix}`;
  const rangeLabelId = `${baseId}-label`;
  const fiscalYearSelectLabelId = `${baseId}-fiscal-year-label`;
  const toggleLabelId = `${baseId}-toggle-label`;
  const [showFiscalYear, setShowFiscalYear] = useState(
    initialSelectedOption !== null && initialSelectedOption !== 'customRange',
  );

  const generateArrayOfYears = (min: number, max: number) => {
    const years = [];

    for (let i = max; i >= min; i -= 1) {
      years.push(i);
    }
    return years;
  };
  const mapIntegersToFY = (): IDropdownOption[] => {
    const currentYear = new Date().getFullYear();
    const fiscalYearsOptions = generateArrayOfYears(currentYear - 9, currentYear + 2);
    const companiesTags = fiscalYearsOptions.map((fy) => {
      const fyStartDate = new Date(`${fy - 1}-07-01 12:00`);
      const fyEndDate = new Date(`${fy}-06-30 12:00`);
      return {
        key: fy.toString(),
        text: `FY${String(fy).slice(-2)} - ${moment(momentUtc(fyStartDate)).format(
          StatDateFormats.DaySimple,
        )} - ${moment(momentUtc(fyEndDate)).format(StatDateFormats.DaySimple)}`,
        data: {
          fyStartDate,
          fyEndDate,
        },
      };
    });
    companiesTags.unshift({
      key: 'customRange',
      text: '--',
      data: { fyStartDate: new Date(), fyEndDate: new Date() },
    });
    return companiesTags;
  };
  const datePickerStyles = { width: 150 };
  return (
    <FluentProvider theme={webLightTheme}>
      <Stack tokens={{ childrenGap: 10 }}>
        <Label id={rangeLabelId}>{label}</Label>
        {!showFiscalYear && (
          <Stack horizontal tokens={{ childrenGap: 5 }}>
            <DatePicker
              placeholder="From"
              aria-label={label}
              value={initialStartDate}
              onSelectDate={(date) => {
                onUpdateDate(date, initialEndDate, 'customRange');
              }}
              allowTextInput
              style={datePickerStyles}
            />
            <DatePicker
              placeholder="To"
              value={initialEndDate}
              onSelectDate={(date) => {
                onUpdateDate(initialStartDate, date, 'customRange');
              }}
              allowTextInput
              style={datePickerStyles}
            />
          </Stack>
        )}
        {showFiscalYear && (
          <FluentProvider theme={webLightTheme}>
            <Label id={fiscalYearSelectLabelId}>Fiscal Year</Label>
            <Select
              placeholder="Select a fiscal year"
              aria-labelledby={`${rangeLabelId} ${fiscalYearSelectLabelId}`}
              value={initialSelectedOption || 'customRange'}
              onChange={(event: React.ChangeEvent<HTMLSelectElement>): void => {
                const selectedOption = mapIntegersToFY().find(
                  (opt) => opt.key === event.target.value,
                );
                if (selectedOption && selectedOption.key !== 'customRange') {
                  const selectedStartDate = selectedOption.fyStartDate;
                  const selectedEndDate = selectedOption.fyEndDate;
                  onUpdateDate(selectedStartDate, selectedEndDate, selectedOption.key as string);
                } else {
                  onUpdateDate(null, null, null);
                }
              }}
            >
              {mapIntegersToFY().map((option) => (
                <option key={option.key} value={option.key}>
                  {option.text}
                </option>
              ))}
            </Select>
          </FluentProvider>
        )}
        <FluentProvider theme={webLightTheme}>
          <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 10 }}>
            <Label id={toggleLabelId}>Show fiscal year selector</Label>
            <Switch
              aria-labelledby={`${rangeLabelId} ${toggleLabelId}`}
              label={showFiscalYear ? 'On' : 'Off'}
              defaultChecked={showFiscalYear}
              onChange={() => {
                const selectedOption = showFiscalYear ? 'customRange' : null;
                onUpdateDate(initialStartDate, initialEndDate, selectedOption);
                setShowFiscalYear(!showFiscalYear);
              }}
            />
          </Stack>
        </FluentProvider>
      </Stack>
    </FluentProvider>
  );
};
export default ProcessSearchFiscalYearPicker;
